/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Utility functions for JSON path operations in the Widget system
 */

/**
 * Extracts a value from an object using a JSON path
 * Supports dot notation and array indexing
 * Examples: "data.items[0].name", "user.profile.email", "metrics.total"
 */
export const extractValueFromPath = (obj: any, path: string): any => {
  if (!path || !obj) {
    return undefined;
  }

  try {
    // Handle array notation like "data.items[0].name"
    const cleanPath = path.replace(/\[(\d+)\]/g, '.$1');
    const keys = cleanPath.split('.');

    let current = obj;
    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  } catch (error) {
    console.warn(`Failed to extract value from path "${path}":`, error);
    return undefined;
  }
};

/**
 * Sets a value in an object using a JSON path
 * Creates intermediate objects/arrays as needed
 */
export const setValueAtPath = (obj: any, path: string, value: any): any => {
  if (!path) {
    return obj;
  }

  try {
    const cleanPath = path.replace(/\[(\d+)\]/g, '.$1');
    const keys = cleanPath.split('.');

    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current)) {
        // Check if next key is numeric (array index)
        const nextKey = keys[i + 1];
        if (/^\d+$/.test(nextKey)) {
          current[key] = [];
        } else {
          current[key] = {};
        }
      }
      current = current[key];
    }

    const lastKey = keys[keys.length - 1];
    current[lastKey] = value;

    return obj;
  } catch (error) {
    console.warn(`Failed to set value at path "${path}":`, error);
    return obj;
  }
};

/**
 * Checks if a string is a expression template (contains {{ }})
 */
export const isExpressionTemplate = (value: any): boolean => {
  return (
    typeof value === 'string' && value.includes('{{') && value.includes('}}')
  );
};

/**
 * Checks if a string is a direct field reference (starts with $.)
 */
export const isDirectFieldReference = (value: any): boolean => {
  return typeof value === 'string' && value.startsWith('$.');
};

/**
 * Checks if a string is a source-specific field reference (e.g., $.source_0.field)
 */
export const isSourceSpecificReference = (value: any): boolean => {
  return typeof value === 'string' && /^\$\.source_\d+\./.test(value);
};

/**
 * Checks if a string contains any field references (either $. or {{$.}})
 */
export const containsFieldReference = (value: any): boolean => {
  return (
    typeof value === 'string' && (value.includes('$.') || value.includes('{{'))
  );
};

/**
 * Checks if any value in an array contains field references
 */
export const arrayContainsFieldReference = (value: any[]): boolean => {
  return value.some((item) => containsAnyFieldReference(item));
};

/**
 * Checks if any value in an object contains field references
 */
export const objectContainsFieldReference = (
  value: Record<string, any>,
): boolean => {
  return Object.values(value).some((item) => containsAnyFieldReference(item));
};

/**
 * Checks if any value (string, array, or object) contains field references
 * This is the main function to use for checking nested structures
 */
export const containsAnyFieldReference = (value: any): boolean => {
  if (typeof value === 'string') {
    return containsFieldReference(value);
  }

  if (Array.isArray(value)) {
    return arrayContainsFieldReference(value);
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return objectContainsFieldReference(value);
  }

  return false;
};

/**
 * Extracts a direct field reference value
 * Returns undefined if the field cannot be found
 */
export const extractDirectFieldValue = (
  fieldReference: string,
  dataResults: Record<string, any>,
): any => {
  if (!isDirectFieldReference(fieldReference)) {
    return undefined;
  }

  const field = fieldReference.slice(2); // Remove '$.'

  // Check if it's a source-specific reference (e.g., $.source_0.field)
  if (isSourceSpecificReference(fieldReference)) {
    const parts = field.split('.');
    const sourceKey = parts[0]; // source_0, source_1, etc.
    const fieldPath = parts.slice(1).join('.'); // field path

    const sourceData = dataResults[sourceKey];
    if (sourceData) {
      return extractValueFromPath(sourceData, fieldPath);
    }
    return undefined;
  }

  // Generic field reference - search all sources
  for (const [_, data] of Object.entries(dataResults)) {
    const value = extractValueFromPath(data, field);
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
};

/**
 * Extracts all JSON path templates from a string
 * Returns an array of paths found
 */
export const extractJsonPaths = (template: string): string[] => {
  const matches = template.match(/\{\{([^}]+)\}\}/g);
  if (!matches) {
    return [];
  }

  return matches.map((match) => match.slice(2, -2).trim());
};

/**
 * Replaces JSON path templates in a string with actual values
 */
export const replaceJsonPaths = (
  template: string,
  dataResults: Record<string, any>,
): string => {
  return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    const trimmedPath = path.trim();

    // Find the data source that contains this path
    for (const [_, data] of Object.entries(dataResults)) {
      const value = extractValueFromPath(data, trimmedPath);
      if (value !== undefined) {
        return String(value);
      }
    }

    // Return original template if no value found
    return match;
  });
};

/**
 * Replaces field references in a string with actual values
 * Supports both $.field and {{$.field}} syntax
 * Returns undefined if any field reference cannot be resolved
 */
export const replaceFieldReferences = (
  template: string,
  dataResults: Record<string, any>,
): string | undefined => {
  let hasUnresolvedReferences = false;

  // First replace {{$.field}} templates
  let result = template.replace(/\{\{\$\.([^}]+)\}\}/g, (match, field) => {
    const trimmedField = field.trim();

    // Check if it's a source-specific reference (e.g., {{$.source_0.field}})
    if (trimmedField.startsWith('source_')) {
      const parts = trimmedField.split('.');
      const sourceKey = parts[0]; // source_0, source_1, etc.
      const fieldPath = parts.slice(1).join('.'); // field path

      const sourceData = dataResults[sourceKey];
      if (sourceData) {
        const value = extractValueFromPath(sourceData, fieldPath);
        if (value !== undefined) {
          return String(value);
        }
      }
      hasUnresolvedReferences = true;
      return '';
    }

    // Generic field reference - search all sources
    for (const [_, data] of Object.entries(dataResults)) {
      const value = extractValueFromPath(data, trimmedField);
      if (value !== undefined) {
        return String(value);
      }
    }

    // Mark as unresolved and return empty string
    hasUnresolvedReferences = true;
    return '';
  });

  // Then replace direct $.field references
  result = result.replace(
    /\$\.([a-zA-Z_$][a-zA-Z0-9_$]*(\.[a-zA-Z_$][a-zA-Z0-9_$]*|\[\d+\])*)/g,
    (match, field) => {
      // Find the data source that contains this field
      for (const [_, data] of Object.entries(dataResults)) {
        const value = extractValueFromPath(data, field);
        if (value !== undefined) {
          return String(value);
        }
      }

      // Mark as unresolved and return empty string
      hasUnresolvedReferences = true;
      return '';
    },
  );

  // Return undefined if any references couldn't be resolved
  return hasUnresolvedReferences ? undefined : result;
};

/**
 * Validates if a JSON path is well-formed
 */
export const validateJsonPath = (path: string): boolean => {
  if (!path || typeof path !== 'string') {
    return false;
  }

  // Basic validation - can be enhanced with more complex rules
  const validPattern =
    /^[a-zA-Z_$][a-zA-Z0-9_$]*(\.[a-zA-Z_$][a-zA-Z0-9_$]*|\[\d+\])*$/;
  return validPattern.test(path);
};

/**
 * Flattens an object into key-value pairs with dot notation
 * Useful for debugging and data inspection
 */
export const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
  const flattened: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, newKey));
    } else {
      flattened[newKey] = value;
    }
  }

  return flattened;
};

/**
 * Recursively replaces field references in any value (string, array, or object)
 * This is the main function to use for resolving nested structures
 */
export const replaceAnyFieldReferences = (
  value: any,
  dataResults: Record<string, any>,
): any => {
  if (typeof value === 'string') {
    return replaceFieldReferences(value, dataResults);
  }

  if (Array.isArray(value)) {
    return value.map((item) => replaceAnyFieldReferences(item, dataResults));
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const resolvedObj: Record<string, any> = {};
    for (const [key, item] of Object.entries(value)) {
      const resolvedValue = replaceAnyFieldReferences(item, dataResults);
      if (resolvedValue !== undefined) {
        resolvedObj[key] = resolvedValue;
      }
    }
    return resolvedObj;
  }

  return value;
};
